// BEGIN FILE: src/constants/familyHouseMapping.ts

/**
 * Vedic Astrology House-to-Family Relationship Mapping
 * Based on traditional Bhavat Bhavam (house from house) principles
 * 
 * Each house represents specific family relationships:
 * H1 - Grandfathers (maternal), Spouse's Grandmother
 * H2 - Overall Family
 * H3 - Siblings, Father-in-law
 * H4 - Mother
 * H5 - Grandfathers (paternal), Spouse's Grandfather
 * H6 - Mother's Siblings (Mama/Maasi), Spouse's Grandmothers, Servants
 * H7 - Spouse, Grandmother (maternal)
 * H8 - Spouse's Family
 * H9 - Father, Spouse's Siblings
 * H10 - Mother-in-law
 * H11 - Friends, Spouse's Grandfather (paternal)
 * H12 - Grandmother (paternal)
 */

export type HouseNum = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12;

// UserForm.tsx relationship values
export type Relationship = 
  | 'Self' 
  | 'Spouse' 
  | 'Father' 
  | 'Mother' 
  | 'Son' 
  | 'Daughter' 
  | 'Brother' 
  | 'Sister' 
  | 'Grandfather (Dada)' 
  | 'Grandmother (Daadi)' 
  | 'Grandfather (Nana)' 
  | 'Grandmother (Naani)' 
  | 'Uncle' 
  | 'Aunt' 
  | 'Other';

export interface FamilyRelationshipInfo {
  displayName: string;       // User-facing name
  dbValue: Relationship;     // Value stored in birth_profiles.relationship
  description?: string;      // Optional explanation
}

/**
 * Maps house numbers to family relationships signified by that house
 * Note: Spouse's family members are tracked via spouse's own chart (no need for separate entries)
 */
export const HOUSE_TO_FAMILY: Record<HouseNum, FamilyRelationshipInfo[]> = {
  1: [
    { displayName: 'Grandfather (Nana ji)', dbValue: 'Grandfather (Nana)', description: 'Maternal grandfather' },
  ],
  2: [
    { displayName: 'Overall Family', dbValue: 'Other', description: 'Family unit, wealth accumulation' },
  ],
  3: [
    { displayName: 'Siblings', dbValue: 'Brother' },
    { displayName: 'Siblings', dbValue: 'Sister' },
  ],
  4: [
    { displayName: 'Mother', dbValue: 'Mother' },
  ],
  5: [
    { displayName: 'Grandfather (Dada ji)', dbValue: 'Grandfather (Dada)', description: 'Paternal grandfather' },
    { displayName: 'Children', dbValue: 'Son' },
    { displayName: 'Children', dbValue: 'Daughter' },
  ],
  6: [
    { displayName: 'Mother\'s Siblings (Mama/Maasi)', dbValue: 'Uncle', description: 'Mother\'s siblings' },
    { displayName: 'Mother\'s Siblings (Mama/Maasi)', dbValue: 'Aunt', description: 'Mother\'s siblings' },
  ],
  7: [
    { displayName: 'Spouse', dbValue: 'Spouse' },
    { displayName: 'Grandmother (Naani ji)', dbValue: 'Grandmother (Naani)', description: 'Maternal grandmother' },
  ],
  8: [
    { displayName: 'Spouse\'s Family', dbValue: 'Spouse', description: 'View via spouse\'s chart' },
  ],
  9: [
    { displayName: 'Father', dbValue: 'Father' },
  ],
  10: [
    { displayName: 'Mother-in-law', dbValue: 'Spouse', description: 'View via spouse\'s chart' },
  ],
  11: [
    { displayName: 'Friends', dbValue: 'Other', description: 'Social circle, gains' },
  ],
  12: [
    { displayName: 'Grandmother (Daadi ji)', dbValue: 'Grandmother (Daadi)', description: 'Paternal grandmother' },
  ],
};

/**
 * Reverse mapping: relationship → primary house(s)
 * Useful for navigating from family member to their signifying house
 */
export const FAMILY_TO_HOUSE: Partial<Record<Relationship, HouseNum[]>> = {
  Mother: [4],
  Father: [9],
  Spouse: [7, 8, 10],  // H7 (spouse), H8 (spouse's family), H10 (mother-in-law via spouse)
  Son: [5],
  Daughter: [5],
  Brother: [3],
  Sister: [3],
  'Grandfather (Dada)': [5],  // Paternal grandfather
  'Grandmother (Daadi)': [12], // Paternal grandmother
  'Grandfather (Nana)': [1],   // Maternal grandfather
  'Grandmother (Naani)': [7],  // Maternal grandmother
  Uncle: [6],
  Aunt: [6],
  Other: [2, 11], // Family unit (H2), Friends (H11)
  Self: [],       // Self is H1 (lagna) but not displayed in family widget
};

/**
 * Life areas to calculate for each family member
 * Based on universal human concerns
 * Standardized to match app-wide life area definitions
 */
export const FAMILY_LIFE_AREAS = [
  'Health',
  'Wealth',
  'Job',
  'Business',
  'Marriage',
  'Children',
] as const;

export type FamilyLifeArea = typeof FAMILY_LIFE_AREAS[number];

/**
 * Get relationships displayed in a specific house
 */
export function getRelationshipsForHouse(house: HouseNum): FamilyRelationshipInfo[] {
  return HOUSE_TO_FAMILY[house] || [];
}

/**
 * Get house(s) associated with a relationship
 */
export function getHousesForRelationship(relationship: Relationship): HouseNum[] {
  return FAMILY_TO_HOUSE[relationship] || [];
}

/**
 * Get display name for a house with relationship context
 */
export function getHouseFamilyTitle(house: HouseNum): string {
  const relationships = getRelationshipsForHouse(house);
  if (relationships.length === 0) return `House ${house}`;
  
  const names = relationships.map(r => r.displayName).join(', ');
  return names;
}
